'use client'

import React, { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ChevronRight, Phone, PhoneOff, Voicemail } from 'lucide-react'
import { useCallContext } from '@/contexts/CallContext'
import { useAppSelector } from '@/store/hooks'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { Switch } from '@/components/ui/switch'
import { useReportFloatingBallFootprint } from '@/hooks/useFloatingBallFootprint'
import type { CallLog } from '@/api/callApi'

function CallRow({ call, onJoin }: { call: CallLog; onJoin: (callSid: string) => Promise<void> }) {
  const { t } = useTranslation('callRouting')
  const [isJoining, setIsJoining] = useState(false)
  const label = call.guestName || call.callerNumber
  const sub = [call.listingName, call.guestName ? call.callerNumber : null].filter(Boolean).join(' · ')

  const handleClick = async () => {
    setIsJoining(true)
    try {
      await onJoin(call.callSid)
    } finally {
      setIsJoining(false)
    }
  }

  return (
    <div className="flex items-center justify-between gap-2 py-2 first:pt-0 last:pb-0 border-b border-border last:border-b-0">
      <div className="min-w-0">
        <p className="text-[13px] font-medium text-foreground truncate">{label}</p>
        <p className="text-[11px] text-muted-foreground truncate">
          {sub || (call.status === 'ringing' ? t('callStatus.ringing') : t('callStatus.inProgress'))}
        </p>
      </div>
      {call.status === 'ringing' ? (
        <button
          onClick={() => void handleClick()}
          disabled={isJoining}
          className={[
            'shrink-0 h-7 px-2.5 rounded-md text-[12px] font-medium transition-colors',
            isJoining
              ? 'bg-muted text-muted-foreground cursor-not-allowed opacity-70'
              : 'bg-primary/10 text-primary hover:bg-primary/20',
          ].join(' ')}
        >
          {isJoining ? t('callStatus.answering') : t('callStatus.answer')}
        </button>
      ) : (
        // No action for an already-answered call: POST /calls/answer claims via
        // an atomic Redis SET NX, so re-answering always 409s, and there is no
        // conference/barge-in backend — a "Join" button here could only fail.
        <span className="shrink-0 text-[11px] text-muted-foreground">
          {call.answeredByName
            ? t('callStatus.answeredBy', { name: call.answeredByName })
            : t('callStatus.answered')}
        </span>
      )}
    </div>
  )
}

// 折叠态跨会话记忆：用户手动收起后保持收起，直到再手动展开。
// 按用户分键：同一台机器换账号时不该继承上一个人的收起状态（localStorage 不随
// 登出清理）。userId 尚未落地（登录窗口期）时退回全局键，行为同旧版。
const COLLAPSE_STORAGE_KEY = 'callStatusIcon.collapsed'
const collapseKeyFor = (userId: string | null | undefined) =>
  userId ? `${COLLAPSE_STORAGE_KEY}.${userId}` : COLLAPSE_STORAGE_KEY

const readCollapsed = (key: string): boolean => {
  try {
    return localStorage.getItem(key) === '1'
  } catch {
    return false
  }
}

// ─── 拖拽位置记忆 ──────────────────────────────────────────────────────────────
//
// 悬浮球固定在右下角，会压住下面的内容（分页条的 Next、抽屉里的提交按钮……）。
// 折叠把手只解决"收起来"，解决不了"我就想让它待在别处"。所以它可拖，并且**记住
// 位置**——每次刷新都跳回右下角的"可拖拽"等于没有可拖拽。
//
// 存的是**到视口右下角的距离**（right/bottom，px），不是 left/top：
// 球本来就是右下角锚定的（`fixed right-4 bottom-4`），存 right/bottom 才能在窗口
// 变窄时保持"离右边多远"这个用户真正在意的关系；存 left 的话缩窗后它会跑到视口外。
const POSITION_STORAGE_KEY = 'callStatusIcon.position'
const positionKeyFor = (userId: string | null | undefined) =>
  userId ? `${POSITION_STORAGE_KEY}.${userId}` : POSITION_STORAGE_KEY

/** 距离视口右/下边缘的像素。null = 从未拖过，用默认的右下角落点。 */
export interface BallPosition {
  right: number
  bottom: number
}

// 球本体 48px（h-12 w-12）。留 8px 余量，保证任何时候至少整颗球都在视口内、
// 且不会贴死边缘（贴死了就抓不住来拖）。
const BALL_SIZE = 48
const EDGE_MARGIN = 8

/** 把位置夹回视口内。窗口缩小后旧位置可能整颗都在视口外，那样球就永远点不到了，
 *  只能重装浏览器数据才能恢复——所以夹取必须在**读取时**也做一遍，不只是拖拽中。 */
export function clampPosition(
  pos: BallPosition,
  viewport: { width: number; height: number },
): BallPosition {
  const maxRight = Math.max(EDGE_MARGIN, viewport.width - BALL_SIZE - EDGE_MARGIN)
  const maxBottom = Math.max(EDGE_MARGIN, viewport.height - BALL_SIZE - EDGE_MARGIN)
  return {
    right: Math.min(Math.max(pos.right, EDGE_MARGIN), maxRight),
    bottom: Math.min(Math.max(pos.bottom, EDGE_MARGIN), maxBottom),
  }
}

/** 解析存下来的位置。任何不是"两个有限数字"的东西一律当没存过——一个 NaN 会变成
 *  `style={{ right: NaN }}`，球直接从页面上消失，比忘记位置糟得多。 */
export function parsePosition(raw: string | null): BallPosition | null {
  if (!raw) return null
  try {
    const parsed = JSON.parse(raw) as Partial<BallPosition> | null
    if (!parsed) return null
    const { right, bottom } = parsed
    if (!Number.isFinite(right) || !Number.isFinite(bottom)) return null
    return { right: right as number, bottom: bottom as number }
  } catch {
    return null
  }
}

const readPosition = (key: string): BallPosition | null => {
  try {
    return parsePosition(localStorage.getItem(key))
  } catch {
    // localStorage 不可用（隐私模式等）
    return null
  }
}

// 层级约定：悬浮球固定在 z-40，必须低于所有弹层。
// 本仓库 Dialog / Sheet / Drawer / Popover / DropdownMenu / Select / Flyout 的
// overlay 与 content 统一是 z-50，嵌套弹层还会用到 z-[60] / z-[70] / z-[150] 等。
// 之前悬浮球用 z-[9997]，恒定漂在所有弹层之上，右下角会压住抽屉表单的提交按钮
// 和评论输入框（用户根本点不到主操作）。降到 z-40 后它仍高于普通页面内容，
// 但任何弹层一开就退到弹层遮罩之下。
// 通话可见性不受影响：来电弹窗 IncomingCallPopup 仍是 z-[9999]（不会漏接），
// 通话中的挂断等控制在 ActiveCallBar（z-[9998]）上，都不依赖这个悬浮球。
export function CallStatusIcon() {
  const { t } = useTranslation('callRouting')
  const { activeCalls, dnd, toggleDnd, answerCall, activeCall, callSystemEnabled } = useCallContext()
  const userId = useAppSelector((state) => state.user.userId)
  const storageKey = collapseKeyFor(userId)
  const posKey = positionKeyFor(userId)
  const [open, setOpen] = useState(false)
  const [collapsed, setCollapsed] = useState(() => readCollapsed(collapseKeyFor(userId)))
  const [position, setPosition] = useState<BallPosition | null>(() =>
    readPosition(positionKeyFor(userId)),
  )
  // 拖拽中：用来抑制拖完那一下的 click（见下面的 suppressClickRef），也用来
  // 给球加一点"抓住了"的视觉反馈。
  const [dragging, setDragging] = useState(false)
  const dragStateRef = useRef<{ pointerId: number; startX: number; startY: number
    startRight: number; startBottom: number; moved: boolean } | null>(null)
  // 一次拖拽结束后，浏览器仍会在 pointerup 之后派发一次 click。不拦住它，
  // 每次拖动都会顺手把 popover 打开/关闭一次。
  const suppressClickRef = useRef(false)

  // userId 是登录后异步落地的（mount 时常为 null），且同页换账号时也会变：
  // 键一变就按新键重读，否则会一直用 mount 那一刻的全局键/前一个用户的值。
  useEffect(() => {
    setCollapsed(readCollapsed(storageKey))
  }, [storageKey])

  useEffect(() => {
    setPosition(readPosition(posKey))
  }, [posKey])

  // 窗口缩小后，存下来的位置可能整颗球都在视口外——那样它就再也点不到了。
  // 每次 resize 夹回视口内（并写回），而不是只在拖拽时夹。
  useEffect(() => {
    const onResize = () => {
      setPosition((prev) => {
        if (!prev) return prev
        const next = clampPosition(prev, {
          width: window.innerWidth,
          height: window.innerHeight,
        })
        if (next.right === prev.right && next.bottom === prev.bottom) return prev
        try {
          localStorage.setItem(posKey, JSON.stringify(next))
        } catch {
          // localStorage 不可用时仅本次会话生效
        }
        return next
      })
    }
    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
  }, [posKey])

  const count = activeCalls.length

  const setCollapsedPersist = (next: boolean) => {
    // 收起时显式关掉 popover：否则 popover 开着时点折叠把手，open 残留 true，
    // 下次展开会意外自动弹开（不能依赖 Radix outside-click 恰好先触发）。
    if (next) setOpen(false)
    setCollapsed(next)
    try {
      localStorage.setItem(storageKey, next ? '1' : '0')
    } catch {
      // localStorage 不可用（隐私模式等）时仅本次会话生效
    }
  }

  // 默认落点：右下角，通话进行中时上移给 ActiveCallBar 让位（旧行为）。
  // 用户拖过一次之后，位置由用户说了算——不再自动上移，因为"我把它放在这儿"
  // 是个明确指令，系统再挪它就是跟用户抢方向盘。
  const restingBottom = activeCall ? 72 : 16
  const effective = position ?? { right: 16, bottom: restingBottom }

  // 停在默认落点时，告诉整页详情(Flyout embedded)右下角被占掉多宽：离右边缘 16 +
  // 球 48 + 16 间隔 = 80（折叠把手 28 贴边，也在这个范围内）。否则整页详情底部操作栏
  // 最右端的 ⋯ 正好被球压住，点到的是通话面板。拖过的位置是用户自己选的，不再让位。
  useReportFloatingBallFootprint(callSystemEnabled && !position ? 16 + 48 + 16 : 0)

  const onPointerDown = (e: React.PointerEvent) => {
    // 只接主键拖拽；右键/中键留给浏览器。
    if (e.button !== 0) return
    dragStateRef.current = {
      pointerId: e.pointerId,
      startX: e.clientX,
      startY: e.clientY,
      startRight: effective.right,
      startBottom: effective.bottom,
      moved: false,
    }
    // setPointerCapture：拖到球外面（甚至拖出窗口再回来）也继续收到 move，
    // 否则快速拖动时指针一离开球，拖拽就断在半路。
    ;(e.currentTarget as HTMLElement).setPointerCapture?.(e.pointerId)
  }

  const onPointerMove = (e: React.PointerEvent) => {
    const state = dragStateRef.current
    if (!state || state.pointerId !== e.pointerId) return
    const dx = e.clientX - state.startX
    const dy = e.clientY - state.startY
    // 5px 死区：手抖不该把一次点击变成一次拖拽（和 SimpleTable 列拖拽同一个阈值）。
    if (!state.moved && Math.abs(dx) < 5 && Math.abs(dy) < 5) return
    if (!state.moved) {
      state.moved = true
      setDragging(true)
      // 拖拽一开始就关掉 popover：拖着一个跟着跑的弹层没有意义，而且 Radix 的
      // 定位是按触发器算的，拖动时会一路重算。
      setOpen(false)
    }
    // right/bottom 与鼠标方向相反：往左拖 = 离右边缘更远。
    setPosition(
      clampPosition(
        { right: state.startRight - dx, bottom: state.startBottom - dy },
        { width: window.innerWidth, height: window.innerHeight },
      ),
    )
  }

  const endDrag = (e: React.PointerEvent) => {
    const state = dragStateRef.current
    if (!state || state.pointerId !== e.pointerId) return
    dragStateRef.current = null
    ;(e.currentTarget as HTMLElement).releasePointerCapture?.(e.pointerId)
    if (!state.moved) return // 只是一次点击，位置没动，也不该写 localStorage
    setDragging(false)
    suppressClickRef.current = true
    setPosition((prev) => {
      if (prev) {
        try {
          localStorage.setItem(posKey, JSON.stringify(prev))
        } catch {
          // localStorage 不可用时仅本次会话生效
        }
      }
      return prev
    })
  }

  // 拖完那一下的 click 要吞掉，否则每次拖动都顺带开/关一次 popover。
  const onClickCapture = (e: React.MouseEvent) => {
    if (!suppressClickRef.current) return
    suppressClickRef.current = false
    e.preventDefault()
    e.stopPropagation()
  }

  const dragHandlers = {
    onPointerDown,
    onPointerMove,
    onPointerUp: endDrag,
    onPointerCancel: endDrag,
    onClickCapture,
  }

  // 权限/租户任一闸没过：整个悬浮球不渲染（hooks 已全部无条件调用完毕）。
  if (!callSystemEnabled) return null

  // 折叠态：贴右边缘的半圆小把手，不遮内容；有活跃通话时仍显示计数徽标,
  // 来电弹窗（IncomingCallPopup）不受折叠影响，不会漏接。
  if (collapsed) {
    // 折叠态贴着右边缘（right: 0），只沿垂直方向记忆位置——半圆把手的整个意义
    // 就是"贴边不占地方"，让它横向自由移动会把它变成第二颗悬浮球。
    return (
      <div
        className="fixed right-0 z-40"
        style={{ bottom: position ? position.bottom : (activeCall ? 72 : 16) }}
      >
        <button
          onClick={() => setCollapsedPersist(false)}
          className={[
            'relative h-12 w-7 rounded-l-full shadow-xl flex items-center justify-start pl-1.5 transition-opacity',
            dnd ? 'bg-muted text-muted-foreground opacity-60' : 'bg-primary text-primary-foreground opacity-100',
          ].join(' ')}
          aria-label={t('callStatus.expand')}
        >
          <Phone className="h-4 w-4" />
          {count > 0 && (
            <span className="absolute -top-1 left-0 h-5 min-w-5 px-1 rounded-full bg-destructive text-destructive-foreground text-[11px] font-semibold flex items-center justify-center">
              {count}
            </span>
          )}
        </button>
      </div>
    )
  }

  const handleJoin = async (callSid: string) => {
    // answerCall (useCallSocket.ts) always resolves, even on failure: it
    // internally catches its own error, shows a `toast.error(...)`, and
    // returns normally with no thrown error or return value distinguishing
    // success from failure. Auto-closing here on every settle would also
    // close on failure, hiding a call that's still ringing/active. Trade-off:
    // we give up auto-close-on-success and rely on manual dismissal (Radix
    // Popover already closes on outside click / Escape) so a failed
    // attempt never disappears behind a closed popover.
    await answerCall(callSid)
  }

  return (
    <div
      className={[
        'group fixed z-40',
        // 拖拽中关掉过渡：否则球会"追"着指针跑，手感像在拉皮筋。
        dragging ? '' : 'transition-[bottom,right] duration-200',
      ].join(' ')}
      style={{ right: effective.right, bottom: effective.bottom }}
    >
      {/* hover 才出现的折叠把手：收起成贴边半圆，解决悬浮球遮挡内容的问题 */}
      <button
        onClick={() => setCollapsedPersist(true)}
        className="absolute -top-1.5 -left-1.5 z-10 h-5 w-5 rounded-full bg-card border border-border shadow flex items-center justify-center opacity-0 group-hover:opacity-100 focus-visible:opacity-100 transition-opacity"
        aria-label={t('callStatus.collapse')}
      >
        <ChevronRight className="h-3 w-3 text-muted-foreground" />
      </button>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            {...dragHandlers}
            data-testid="call-status-ball"
            // touch-none：移动端上不加这个，浏览器会把拖拽当成页面滚动，
            // pointermove 直接被 scroll 抢走。
            className={[
              'relative h-12 w-12 rounded-full shadow-xl flex items-center justify-center transition-opacity touch-none',
              dragging ? 'cursor-grabbing' : 'cursor-grab',
              dnd ? 'bg-muted text-muted-foreground opacity-60' : 'bg-primary text-primary-foreground opacity-100',
            ].join(' ')}
            aria-label={count > 0 ? t('callStatus.iconLabelWithCount', { count }) : t('callStatus.iconLabel')}
          >
            <Phone className="h-5 w-5" />
            {count > 0 && (
              <span className="absolute -top-1 -right-1 h-5 min-w-5 px-1 rounded-full bg-destructive text-destructive-foreground text-[11px] font-semibold flex items-center justify-center">
                {count}
              </span>
            )}
          </button>
        </PopoverTrigger>
        <PopoverContent align="end" side="top" className="w-80">
          <div className="flex items-center justify-between pb-3 border-b border-border mb-1">
            <div className="flex items-center gap-2">
              {dnd ? <PhoneOff className="h-4 w-4 text-muted-foreground" /> : <Phone className="h-4 w-4 text-primary" />}
              <span className="text-[13px] font-medium text-foreground">{t('callStatus.dnd')}</span>
            </div>
            <Switch checked={dnd} onCheckedChange={() => void toggleDnd()} />
          </div>

          <div className="max-h-72 overflow-y-auto">
            {activeCalls.length === 0 ? (
              <div className="flex flex-col items-center gap-1.5 py-6 text-muted-foreground">
                <Voicemail className="h-5 w-5" />
                <p className="text-[12px]">{t('callStatus.noActiveCalls')}</p>
              </div>
            ) : (
              activeCalls.map((call) => (
                <CallRow key={call.callSid} call={call} onJoin={handleJoin} />
              ))
            )}
          </div>
        </PopoverContent>
      </Popover>
    </div>
  )
}
